import { Action, Reducer } from 'redux';

import {
    ReservationDTO,
    ReservationListFilter,
    TableAvailabilityDTO,
} from 'api/controllers/reservations';

import { matchesReservationFilter } from 'helpers/reservationHelper';
import { ReservationsAction } from './actions';

export interface ReservationsState {
    requests: ReservationDTO[];
    requestsRestaurantId: string | null;
    selectedRequestId: string | null;
    /** Open requests matching the REQUESTS tab filter; filter null means the full `requests` list is shown. */
    requestsView: {
        filter: ReservationListFilter | null;
        items: ReservationDTO[];
        isLoading: boolean;
    };
    /** day and search describe the loaded reservations; day null means all dates. */
    active: {
        day: string | null | undefined;
        search: string;
        reservations: ReservationDTO[];
        isLoading: boolean;
    };
    selectedActiveId: string | null;
    activeDetailsOverride: ReservationDTO | null;
    cancellingId: string | null;
    cancelDialogId: string | null;
    requestDialogId: string | null;
    requestClosurePending: Partial<Record<string, 'decline' | 'cancel'>>;
    requestRefresh: Partial<Record<string, 'loading' | 'failed'>>;
    changeRevision: number;
    availability: {
        key: string | null;
        tables: TableAvailabilityDTO[];
    };
}

export const initialState: ReservationsState = {
    requests: [],
    requestsRestaurantId: null,
    selectedRequestId: null,
    requestsView: { filter: null, items: [], isLoading: false },
    active: { day: undefined, search: '', reservations: [], isLoading: false },
    selectedActiveId: null,
    activeDetailsOverride: null,
    cancellingId: null,
    cancelDialogId: null,
    requestDialogId: null,
    requestClosurePending: {},
    requestRefresh: {},
    changeRevision: 0,
    availability: { key: null, tables: [] },
};

function isOpenRequest(reservation: ReservationDTO): boolean {
    return reservation.status === 'Requested' && !reservation.isArchived;
}

function matchesActive(state: ReservationsState, reservation: ReservationDTO): boolean {
    const { day, search } = state.active;
    return day !== undefined && matchesReservationFilter(reservation, { day, search });
}

function applyToRequestsView(
    view: ReservationsState['requestsView'],
    reservation: ReservationDTO
): ReservationsState['requestsView'] {
    if (!view.filter) {
        return view;
    }
    const items = view.items.filter((item) => item.id !== reservation.id);
    if (isOpenRequest(reservation) && matchesReservationFilter(reservation, view.filter)) {
        const index = view.items.findIndex((item) => item.id === reservation.id);
        items.splice(index < 0 ? items.length : index, 0, reservation);
    }
    return { ...view, items };
}

function withoutRequest(state: ReservationsState, id: string): ReservationsState {
    return {
        ...state,
        requests: state.requests.filter((request) => request.id !== id),
        requestsView: {
            ...state.requestsView,
            items: state.requestsView.items.filter((request) => request.id !== id),
        },
        selectedRequestId: state.selectedRequestId === id ? null : state.selectedRequestId,
    };
}

export const reducer: Reducer<ReservationsState> = (
    state: ReservationsState | undefined,
    incomingAction: Action
): ReservationsState => {
    if (state === undefined) {
        return initialState;
    }

    if (incomingAction.type === 'USER_SET_CURRENT_RESTAURANT_START') {
        return {
            ...state,
            activeDetailsOverride: null,
            cancellingId: null,
            cancelDialogId: null,
            requestDialogId: null,
            requestClosurePending: {},
            requestRefresh: {},
        };
    }

    const action = incomingAction as ReservationsAction;

    switch (action.type) {
        case 'RESERVATION_REQUESTS_SET': {
            const { filter } = state.requestsView;
            const isSameRestaurant =
                state.requestsRestaurantId === null ||
                state.requestsRestaurantId === action.restaurantId;
            return {
                ...state,
                requests: action.requests,
                requestsRestaurantId: action.restaurantId,
                requestsView:
                    filter && isSameRestaurant
                        ? {
                              ...state.requestsView,
                              items: action.requests.filter(
                                  (request) =>
                                      isOpenRequest(request) &&
                                      matchesReservationFilter(request, filter)
                              ),
                          }
                        : isSameRestaurant
                        ? state.requestsView
                        : initialState.requestsView,
                selectedRequestId: action.requests.some(
                    (request) => request.id === state.selectedRequestId
                )
                    ? state.selectedRequestId
                    : null,
            };
        }
        case 'RESERVATION_REQUESTS_VIEW_LOAD':
            return { ...state, requestsView: { ...state.requestsView, isLoading: true } };
        case 'RESERVATION_REQUESTS_VIEW_SET':
            return {
                ...state,
                requestsView: { filter: action.filter, items: action.requests, isLoading: false },
            };
        case 'RESERVATION_REQUESTS_VIEW_FAIL':
            return { ...state, requestsView: { ...state.requestsView, isLoading: false } };
        case 'RESERVATION_REQUEST_SELECT':
            return { ...state, selectedRequestId: action.id };
        case 'RESERVATION_REQUEST_SAVED':
            return reducer(state, {
                type: 'RESERVATION_REALTIME_UPDATE',
                reservation: action.reservation,
            });
        case 'RESERVATION_REQUEST_REMOVED': {
            const next = withoutRequest(state, action.id);
            const reservations = next.active.reservations.filter(
                (item) => item.id !== action.id || item.status !== 'Requested'
            );
            return {
                ...next,
                active: { ...next.active, reservations },
                selectedActiveId:
                    next.selectedActiveId === action.id &&
                    !reservations.some((item) => item.id === action.id)
                        ? null
                        : next.selectedActiveId,
                changeRevision: state.changeRevision + 1,
            };
        }
        case 'RESERVATION_CONFIRMED': {
            const { reservation } = action;
            const next = withoutRequest(state, reservation.id);
            return {
                ...next,
                selectedActiveId: reservation.id,
                activeDetailsOverride: null,
                changeRevision: state.changeRevision + 1,
                active: matchesActive(next, reservation)
                    ? {
                          ...next.active,
                          reservations: next.active.reservations
                              .filter((item) => item.id !== reservation.id)
                              .concat(reservation),
                      }
                    : next.active,
            };
        }
        case 'RESERVATION_REALTIME_UPDATE': {
            const { reservation } = action;
            const requestRefresh = { ...state.requestRefresh };
            delete requestRefresh[reservation.id];
            let next = { ...state, requestRefresh };
            if (state.requestsRestaurantId === reservation.restaurantId) {
                if (isOpenRequest(reservation)) {
                    const exists = state.requests.some((item) => item.id === reservation.id);
                    next = {
                        ...next,
                        requests: exists
                            ? state.requests.map((item) =>
                                  item.id === reservation.id ? reservation : item
                              )
                            : state.requests.concat(reservation),
                        requestsView: applyToRequestsView(state.requestsView, reservation),
                    };
                } else {
                    next = withoutRequest(next, reservation.id);
                }
            }

            const isActive =
                !reservation.isArchived &&
                (reservation.status === 'Requested' ||
                    reservation.status === 'Confirmed' ||
                    reservation.status === 'Seated');
            const isCancelDialogOpen = state.cancelDialogId === reservation.id;
            const matchesLoaded = matchesActive(next, reservation);
            const isLoaded = next.active.reservations.some((item) => item.id === reservation.id);
            if (isLoaded || (isActive && matchesLoaded)) {
                const reservations = next.active.reservations.filter(
                    (item) => item.id !== reservation.id
                );
                if (isActive && matchesLoaded) {
                    reservations.push(reservation);
                }
                next = {
                    ...next,
                    active: { ...next.active, reservations },
                    selectedActiveId:
                        next.selectedActiveId === reservation.id &&
                        !reservations.some((item) => item.id === reservation.id)
                            ? null
                            : next.selectedActiveId,
                };
            }
            const keepDetails =
                (action.keepActiveDetails ||
                    state.cancellingId === reservation.id ||
                    isCancelDialogOpen) &&
                (!isActive || !matchesLoaded || isCancelDialogOpen) &&
                state.selectedActiveId === reservation.id;
            if (keepDetails || state.activeDetailsOverride?.id === reservation.id) {
                next = {
                    ...next,
                    selectedActiveId: reservation.id,
                    activeDetailsOverride: isActive && matchesLoaded ? null : reservation,
                };
            }
            return { ...next, changeRevision: state.changeRevision + 1 };
        }
        case 'RESERVATION_ACTIVE_LOAD': {
            const isSameList =
                action.day === state.active.day && action.search === state.active.search;
            return {
                ...state,
                activeDetailsOverride: isSameList ? state.activeDetailsOverride : null,
                active:
                    isSameList || action.keepPrevious
                        ? { ...state.active, isLoading: true }
                        : {
                              day: action.day,
                              search: action.search,
                              reservations: [],
                              isLoading: true,
                          },
            };
        }
        case 'RESERVATION_ACTIVE_SET':
            return {
                ...state,
                active: {
                    day: action.day,
                    search: action.search,
                    reservations: action.reservations,
                    isLoading: false,
                },
            };
        case 'RESERVATION_ACTIVE_FAIL':
            return { ...state, active: { ...state.active, isLoading: false } };
        case 'RESERVATION_ACTIVE_SELECT':
            return {
                ...state,
                selectedActiveId: action.id,
                activeDetailsOverride: null,
                cancelDialogId: action.id === state.cancelDialogId ? state.cancelDialogId : null,
            };
        case 'RESERVATION_CANCEL_PENDING':
            return {
                ...state,
                cancellingId: action.isPending
                    ? action.id
                    : state.cancellingId === action.id
                    ? null
                    : state.cancellingId,
            };
        case 'RESERVATION_CANCEL_DIALOG':
            return { ...state, cancelDialogId: action.id };
        case 'RESERVATION_REQUEST_DIALOG':
            return { ...state, requestDialogId: action.id };
        case 'RESERVATION_REQUEST_CLOSURE_PENDING': {
            const requestClosurePending = { ...state.requestClosurePending };
            if (action.isPending) {
                requestClosurePending[action.id] = action.operation;
            } else if (requestClosurePending[action.id] === action.operation) {
                delete requestClosurePending[action.id];
            }
            return { ...state, requestClosurePending };
        }
        case 'RESERVATION_REQUEST_REFRESH':
            return {
                ...state,
                requestDialogId: state.requestDialogId === action.id ? null : state.requestDialogId,
                requestRefresh: { ...state.requestRefresh, [action.id]: action.status },
            };
        case 'RESERVATION_AVAILABILITY_LOAD':
            return action.key === state.availability.key
                ? state
                : { ...state, availability: { key: action.key, tables: [] } };
        case 'RESERVATION_AVAILABILITY_SET':
            return action.key === state.availability.key
                ? { ...state, availability: { key: action.key, tables: action.tables } }
                : state;
        case 'RESERVATION_AVAILABILITY_CLEAR':
            return { ...state, availability: initialState.availability };
        default:
            return state;
    }
};
